import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Chapter, useSprintProductReportsBySprint } from '@data';
import { useListSprints } from '@data/hooks/sprints';
import { useProductIncidents } from '@data/hooks/incidents';
import { Loader } from '@ui';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ui/components/ui/select';
import { useOrganizationId } from '@app/utils/hooks';
import { Pagination } from '@app/components/core/pagination';
import { useArrivalScroll } from '@app/components/core/arrivalScroll';
import {
  BUDGET_SECTIONS,
  SECTION_PRESETS,
  emptyBudget,
  rateIn,
  readBudget,
  teamAt,
  VersionTeam,
  writeBudget,
} from './budget';
import {
  fractionalRate,
  isFractional,
  readFractionalHours,
  readRoster,
  writeFractionalHours,
  writeRoster,
  type FractionalPerson,
} from './fractional';
import { readYearDiscounts, writeDiscount, type SprintDiscounts } from './discounts';
import { fixedChargeIn } from './pricing';
import { readYearReleases, retentionAt, writeRelease } from './retention';

import { QuarterLine, quarterOf, readQuarterCosts, writeQuarterCosts } from './quarterCosts';
import { hoursByMember } from './spend';
import WorkforceGrid, { type SprintHours } from './WorkforceGrid';
import BudgetYearHeader from './BudgetYearHeader';
import { useProductBudgetSheet } from './useBudgetSheet';
import { useSettledSprints } from './useSettledSprints';
import { useProductViewer } from './productViewer';
import OutOfPocketCard from './OutOfPocketCard';
import StatementOfWorkCard from './StatementOfWorkCard';
import IncidentCostsCard from './IncidentCostsCard';
import { incidentCostsByQuarter } from './incidentCosts';
import { readServiceHours } from './serviceLevels';
import { useUnsavedWork } from './unsavedWork';
import { useYearPeople } from './yearPeople';
import { FIRST_MONEY_YEAR, noSprintsKey, useYearChoice } from './yearChoice';

/** The forecast mixes the two by whether a sprint is closed; the others read every sprint one way. */
export const COST_READINGS = ['forecast', 'projected', 'actual'] as const;
export type CostReading = (typeof COST_READINGS)[number];

/** What a closed sprint cost, and what the rest are projected to: the year as it is heading. */
const ForecastTab = ({
  teams,
  chapters,
  canEdit,
  productName,
  reading,
  onRead,
  activeIds,
  opensOn,
}: {
  teams: VersionTeam[];
  chapters: Chapter[];
  canEdit: boolean;
  productName: string;
  reading: CostReading;
  onRead: (reading: CostReading) => void;
  activeIds?: Set<string>;
  /** The sprint a link asked for: its year and its column are where the grid opens. */
  opensOn?: string;
}): JSX.Element => {
  const { t } = useTranslation();
  const { isClient } = useProductViewer();
  const { guard } = useUnsavedWork();
  const { productId = '' } = useParams<{ productId: string }>();
  const organizationId = useOrganizationId();
  const { sprints, years, year, choose, yearSprints, isLoading } = useYearChoice({ nextYear: true, opensOn });
  // Bumped on save, because the figures live outside React in the product store.
  const [revision, setRevision] = useState(0);

  const columns = useMemo(
    () =>
      yearSprints.map(each => ({
        id: each.id,
        number: each.number,
        quarter: quarterOf(each.startDate),
        startDate: each.startDate,
        endDate: each.endDate,
      })),
    [yearSprints],
  );
  const sprintIds = useMemo(() => yearSprints.map(each => each.id), [yearSprints]);

  // Every version the year touches, or a year before the product's first version draws no rows at all.
  const yearTeams = useMemo(() => {
    const touching = teams.filter(
      team => team.version.startDate <= `${year}-12-31` && team.version.endDate >= `${year}-01-01`,
    );
    if (touching.length || !teams.length) return touching;
    return `${year}-12-31` < teams[0].version.startDate ? teams.slice(0, 1) : teams.slice(-1);
  }, [teams, year]);
  const teamBySprint = useMemo(
    () => Object.fromEntries(columns.map(sprint => [sprint.id, teamAt(teams, sprint.startDate)])),
    [teams, columns],
  );

  const {
    closedSprintIds,
    isLoading: settledLoading,
    isError: settledFailed,
    refetch: settledRetry,
  } = useSettledSprints(organizationId, productId);
  const settled = !settledLoading && !settledFailed;
  const projectedIds = useMemo(
    () => (settled ? columns.filter(sprint => !closedSprintIds.includes(sprint.id)).map(sprint => sprint.id) : []),
    [settled, columns, closedSprintIds],
  );
  const showsProjections = reading === 'projected';
  const shownProjected = { forecast: projectedIds, projected: sprintIds, actual: [] as string[] }[reading];
  const isProjected = (sprintId: string): boolean => shownProjected.includes(sprintId);
  // Actuals alone stop at the last closed sprint: an open one's figures are not actuals yet.
  const gridColumns = reading === 'actual' ? columns.filter(sprint => !projectedIds.includes(sprint.id)) : columns;
  // Saves cover the sprints on screen only, so a hidden one keeps what it holds.
  const onScreen = yearSprints.filter(each => gridColumns.some(column => column.id === each.id));

  const { bySprint, isLoading: reportsLoading } = useSprintProductReportsBySprint(yearSprints, productId);
  const reportedBySprint = useMemo(
    () => Object.fromEntries(yearSprints.map(each => [each.id, hoursByMember(bySprint[each.id] ?? [])])),
    [yearSprints, bySprint],
  );

  // Kept out of the grid since #688: it is charged per quarter, at its own multipliers.
  const {
    bySprint: incidentBySprint,
    isLoading: incidentLoading,
    isError: incidentReportsFailed,
  } = useSprintProductReportsBySprint(yearSprints, productId, { incidentWork: true, enabled: !isClient });
  const { data: incidentList, isError: incidentsFailed } = useProductIncidents(isClient ? undefined : productId);
  const incidentQuarters = useMemo(
    () =>
      incidentCostsByQuarter({
        reports: yearSprints.flatMap(each => incidentBySprint[each.id] ?? []),
        incidents: incidentList?.items ?? [],
        year,
        rateAt: (day, memberId) => rateIn(teamAt(teams, day), memberId),
        serviceAt: day => readServiceHours(productId, teamAt(teams, day)?.version.number),
      }),
    [yearSprints, incidentBySprint, incidentList, year, teams, productId],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const roster = useMemo(() => readRoster(productId, year), [productId, year, revision]);
  // What was spent, the team's reported hours and the bought-in lines typed against them.
  const spentHours: SprintHours = useMemo(
    () =>
      Object.fromEntries(
        yearSprints.map(each => [
          each.id,
          { ...(reportedBySprint[each.id] ?? {}), ...readFractionalHours('spend', productId, each.id) },
        ]),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [yearSprints, reportedBySprint, productId, revision],
  );
  const projectedHours: SprintHours = useMemo(
    () =>
      Object.fromEntries(
        yearSprints.map(each => [
          each.id,
          { ...(readBudget(productId, each.id)?.hours ?? {}), ...readFractionalHours('budget', productId, each.id) },
        ]),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [yearSprints, productId, revision],
  );
  const gridHours: SprintHours = useMemo(
    () =>
      Object.fromEntries(
        yearSprints.map(each => [
          each.id,
          shownProjected.includes(each.id) ? projectedHours[each.id] : spentHours[each.id],
        ]),
      ),
    [yearSprints, shownProjected, projectedHours, spentHours],
  );
  const people = useYearPeople(productId, yearTeams, sprintIds, gridHours);

  const discounts = useMemo(
    () =>
      readYearDiscounts(
        productId,
        yearSprints.map(each => each.id),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [productId, revision, yearSprints],
  );
  const saveDiscounts = (next: SprintDiscounts): void => {
    onScreen.forEach(each => writeDiscount(productId, each.id, next[each.id] ?? null));
    setRevision(current => current + 1);
  };

  const { data: sprintList } = useListSprints({ limit: 400 });
  const fixedCharges = useMemo(() => {
    const values = Object.fromEntries(
      yearSprints
        .map(each => [each.id, fixedChargeIn(teamBySprint[each.id] ?? null, each.id, sprintList?.items ?? [])] as const)
        .filter((entry): entry is readonly [string, number] => entry[1] !== null),
    );
    return Object.keys(values).length ? { values, label: t('pricing.fixedPrice') } : undefined;
  }, [yearSprints, teamBySprint, sprintList, t]);

  const released = useMemo(
    () =>
      readYearReleases(
        productId,
        yearSprints.map(each => each.id),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [productId, revision, yearSprints],
  );
  const saveRelease = (sprintId: string, amount: number): void => {
    writeRelease(productId, sprintId, amount);
    setRevision(current => current + 1);
  };

  const rateFor = (sprintId: string, personId: string): number | null =>
    isFractional(roster, personId) ? fractionalRate(roster, personId) : rateIn(teamBySprint[sprintId], personId);

  // A closed sprint takes its bought-in lines as spent; any other is the projection, people and bought-in alike.
  const saveHours = (hours: SprintHours, fractional: FractionalPerson[]): void => {
    writeRoster(productId, year, fractional);
    const fractionalIds = fractional.map(person => person.id);
    const peopleIds = people.map(person => person.id);
    onScreen.forEach(each => {
      const typed = hours[each.id] ?? {};
      const forFractional = Object.fromEntries(Object.entries(typed).filter(([id]) => fractionalIds.includes(id)));
      if (!isProjected(each.id)) {
        writeFractionalHours('spend', productId, each.id, forFractional);
        return;
      }
      const forMembers = Object.fromEntries(
        Object.entries(typed).filter(([id]) => !fractionalIds.includes(id) && peopleIds.includes(id)),
      );
      const stored = readBudget(productId, each.id) ?? emptyBudget();
      const kept = { ...stored.hours };
      peopleIds.forEach(id => delete kept[id]);
      writeBudget(productId, each.id, { ...stored, hours: { ...kept, ...forMembers } });
      writeFractionalHours('budget', productId, each.id, forFractional);
    });
    setRevision(current => current + 1);
  };

  const costs = useMemo(
    () => readQuarterCosts('spend', productId, year),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [productId, year, revision],
  );
  const saveCosts = (lines: Record<string, QuarterLine[]>): void => {
    writeQuarterCosts('spend', productId, year, { ...costs, ...lines });
    setRevision(current => current + 1);
  };

  const staffed = chapters
    .map(chapter => ({
      key: chapter.key,
      label: chapter.label,
      members: people.filter(person => person.chapterKey === chapter.key),
    }))
    .filter(group => group.members.length > 0);
  const unfiled = people.filter(person => !chapters.some(chapter => chapter.key === person.chapterKey));
  const groups = [...staffed, ...(unfiled.length ? [{ key: '', label: t('budget.noChapter'), members: unfiled }] : [])];

  const sheet = useProductBudgetSheet({
    productId,
    productName,
    year,
    sprints: yearSprints,
    groups,
    teams,
    roster,
    closedSprintIds,
    settledLoading,
    settledFailed,
    settledRetry,
    revision,
  });

  useArrivalScroll(!isLoading && !reportsLoading && !settledLoading);

  // Not year-paged: a statement of work can be signed across a year boundary, or before the cutoff.
  const statements = (
    <StatementOfWorkCard
      productId={productId}
      canEdit={canEdit}
      sprints={sprints.map(sprint => ({
        id: sprint.id,
        number: sprint.number,
        startDate: sprint.startDate.slice(0, 10),
        endDate: sprint.endDate.slice(0, 10),
      }))}
    />
  );

  if (isLoading) return <Loader />;
  if (!yearSprints.length)
    return (
      <div className="space-y-8">
        <p className="text-sm text-muted-foreground">{t(noSprintsKey(sprints), { year: FIRST_MONEY_YEAR })}</p>
        {sprints.length > 0 && statements}
      </div>
    );

  // The projections read neither what was reported nor which sprints are closed, so they wait on neither.
  const needsSettled = reading !== 'projected';
  const gridReady = !needsSettled || (!reportsLoading && settled);

  const readingPicker = (
    <Select
      value={reading}
      onValueChange={next => next && guard(() => onRead(next as CostReading))}
      items={Object.fromEntries(COST_READINGS.map(each => [each, t(`forecast.readings.${each}`)]))}
    >
      <SelectTrigger size="sm" className="min-w-32 text-foreground" aria-label={t('forecast.show')}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent align="end" alignItemWithTrigger={false}>
        {COST_READINGS.map(each => (
          <SelectItem key={each} value={each}>
            {t(`forecast.readings.${each}`)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  return (
    <div className="space-y-8">
      <div className="space-y-4">
        <BudgetYearHeader title={year} figuresFailed={sheet.figuresFailed} onRetryFigures={settledRetry} />

        {/* Where the dashboard's arrows land: the grid, or the bought-in costs under it. */}
        <div id="spend-workforce">
          {!gridReady && !(needsSettled && settledFailed) && <Loader />}
          {/* Which sprints are closed decides every column, so an unread list is no forecast at all. */}
          {needsSettled && settledFailed && <p className="text-sm text-muted-foreground">{t('cardReadFailed')}</p>}
          {gridReady && (
            <WorkforceGrid
              reading={reading}
              gridId={productId}
              opensOn={opensOn}
              title={t(
                { forecast: 'forecast.workforce', projected: 'budget.workforce', actual: 'forecast.actualWorkforce' }[
                  reading
                ],
              )}
              hidesEmptySprints={false}
              totalActualsOnly={reading === 'forecast'}
              totalNote={t(
                { forecast: 'forecast.actualsOnly', projected: 'forecast.projected', actual: 'forecast.actuals' }[
                  reading
                ],
              )}
              cornerControls={readingPicker}
              groups={groups.map(group => ({ key: group.key, label: group.label, people: group.members }))}
              sprints={gridColumns}
              hours={gridHours}
              rateFor={rateFor}
              fractional={{ key: 'fractional', label: t('budget.fractional'), people: roster }}
              editableRows={showsProjections ? 'all' : 'fractional'}
              projectedSprintIds={shownProjected}
              typeableSprintIds={reading === 'forecast' && canEdit && !isClient ? projectedIds : undefined}
              totalColumn
              activeIds={activeIds}
              onSave={(showsProjections ? canEdit : !isClient) ? saveHours : undefined}
              lead="euros"
              // A bill's lines, so they belong to what was spent, not to what was projected.
              {...(showsProjections
                ? {}
                : {
                    openSprintIds: projectedIds,
                    discounts: {
                      values: discounts,
                      label: t('spend.discount'),
                      onSave: isClient ? undefined : saveDiscounts,
                    },
                    fixedCharges,
                    retention: {
                      label: t('spend.retention'),
                      releaseLabel: t('spend.release'),
                      percentFor: (sprintId: string) => retentionAt(productId, teamBySprint[sprintId] ?? null),
                      released,
                      closedSprintIds,
                      onRelease: isClient ? undefined : saveRelease,
                    },
                  })}
            />
          )}
        </div>
      </div>

      <div id="spend-out-of-pocket">
        <OutOfPocketCard
          productId={productId}
          title={t('spend.outOfPocket')}
          hint={t('spend.sectionHint', { year })}
          sections={BUDGET_SECTIONS.map(section => ({
            key: section,
            label: t(`budget.sections.${section}`),
            presets: SECTION_PRESETS[section],
            lines: costs[section],
          }))}
          onSave={isClient ? undefined : saveCosts}
          scope={`spend:${year}`}
          spent
        />
      </div>

      {/* Our price, not the client's: they read incident time on its own report. Nothing booked, no card. */}
      {!isClient && (incidentLoading || incidentQuarters.length > 0 || incidentReportsFailed || incidentsFailed) && (
        <div id="spend-incidents">
          {incidentLoading ? (
            <Loader />
          ) : (
            <IncidentCostsCard
              title={t('spend.incidents.title')}
              hint={t('spend.incidents.hint', { year })}
              emptyText={t('spend.incidents.none', { year })}
              quarters={incidentQuarters}
              isError={incidentReportsFailed || incidentsFailed}
            />
          )}
        </div>
      )}

      {statements}

      <Pagination
        page={years.indexOf(year)}
        pageCount={years.length}
        // Another year is another year's typing: what is unsaved here is asked about first.
        onChange={page => guard(() => choose(years[page]))}
        labels={years}
        reversed
      />
    </div>
  );
};

export default ForecastTab;
